import React, { useEffect, useId, useState } from "react";
import styles from "./OrderLookupCard.module.css";
import {
  isValidEmail,
  isValidZip,
  lookupOrder,
  type OrderView,
} from "../../services/orderLookupService";

/**
 * TRACI-27: "Check an order", drawn by the chat itself with no model in the loop.
 *
 * The email and ZIP live only in this component's state and go straight to the
 * backend's order lookup route; they are never sent as a chat message, never put in
 * the URL, title or storage, and are cleared as soon as the lookup is sent. What
 * comes back is the allowlisted order view, drawn here.
 */

interface OrderLookupCardProps {
  sessionId: string;
  getToken: () => Promise<string | undefined>;
}

type Phase =
  | { kind: "closed" }
  | { kind: "form"; error?: string }
  | { kind: "loading" }
  | { kind: "found"; order: OrderView }
  | { kind: "not_found" };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-17" -> "Oct 17, 2026", without a timezone shift; anything else as given. */
export function formatOrderDate(value?: string): string | undefined {
  if (!value) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}, ${m[1]}` : value;
}

export function OrderSummary({ order }: { order: OrderView }) {
  const dates: Array<[string, string | undefined]> = [
    ["Order date", formatOrderDate(order.order_date)],
    ["Ship date", formatOrderDate(order.ship_date)],
    ["Delivery date", formatOrderDate(order.delivery_date)],
  ];
  return (
    <div className={styles.summary} data-testid="order-summary">
      <p className={styles.status}>{order.status}</p>
      {(order.carrier || order.tracking_number) && (
        <p className={styles.tracking}>
          {order.carrier && <span>{order.carrier}</span>}
          {order.tracking_number && (
            order.tracking_url ? (
              <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className={styles.trackLink}>
                Track {order.tracking_number}
              </a>
            ) : (
              <span className={styles.mono}>{order.tracking_number}</span>
            )
          )}
        </p>
      )}
      <dl className={styles.dates}>
        {dates.filter(([, v]) => v).map(([label, v]) => (
          <div key={label} className={styles.dateRow}>
            <dt>{label}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {order.items.length > 0 && (
        <ul className={styles.items}>
          {order.items.map((item, i) => (
            <li key={i} className={styles.item}>
              <span className={styles.qty}>{item.quantity} x</span>
              <span>
                {item.product}
                {item.size && <span className={styles.size}> {item.size}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function OrderLookupCard({ sessionId, getToken }: OrderLookupCardProps) {
  const [phase, setPhase] = useState<Phase>({ kind: "closed" });
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const emailId = useId();
  const zipId = useId();

  // A new chat session gets a fresh card; nothing from the last one carries over.
  useEffect(() => {
    setPhase({ kind: "closed" });
    setEmail("");
    setZip("");
  }, [sessionId]);

  const close = () => {
    setEmail("");
    setZip("");
    setPhase({ kind: "closed" });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email) || !isValidZip(zip)) {
      setPhase({ kind: "form", error: "Enter the email on the order and a 5-digit ZIP code." });
      return;
    }
    const sentEmail = email;
    const sentZip = zip;
    // Cleared the moment it is sent, so nothing lingers in the page.
    setEmail("");
    setZip("");
    setPhase({ kind: "loading" });
    const result = await lookupOrder(sessionId, sentEmail, sentZip, getToken);
    if (result.kind === "found") setPhase({ kind: "found", order: result.order });
    else if (result.kind === "not_found") setPhase({ kind: "not_found" });
    else setPhase({ kind: "form", error: result.message });
  };

  if (phase.kind === "closed") {
    return (
      <div className={styles.chipRow}>
        <button type="button" className={styles.chip} onClick={() => setPhase({ kind: "form" })}>
          Check an order
        </button>
      </div>
    );
  }

  return (
    <section className={styles.card} aria-label="Check an order" data-testid="order-lookup-card">
      <header className={styles.header}>
        <h2 className={styles.title}>Check an order</h2>
        <button type="button" className={styles.close} onClick={close} aria-label="Close order check">
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      {(phase.kind === "form" || phase.kind === "loading") && (
        <form className={styles.form} onSubmit={submit} noValidate>
          <p className={styles.note}>
            Your email and ZIP go straight to our order system. The chat assistant never sees them.
          </p>
          <label htmlFor={emailId} className={styles.label}>Email on the order</label>
          <input
            id={emailId}
            className={styles.input}
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={phase.kind === "loading"}
            maxLength={254}
          />
          <label htmlFor={zipId} className={styles.label}>Billing ZIP code</label>
          <input
            id={zipId}
            className={styles.input}
            type="text"
            autoComplete="postal-code"
            inputMode="numeric"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            disabled={phase.kind === "loading"}
            maxLength={10}
          />
          {phase.kind === "form" && phase.error && (
            <p className={styles.error} role="alert">{phase.error}</p>
          )}
          <button type="submit" className={styles.submit} disabled={phase.kind === "loading"}>
            {phase.kind === "loading" ? "Checking..." : "Check order"}
          </button>
        </form>
      )}

      {phase.kind === "found" && (
        <>
          <OrderSummary order={phase.order} />
          <button type="button" className={styles.secondary} onClick={() => setPhase({ kind: "form" })}>
            Check another order
          </button>
        </>
      )}

      {phase.kind === "not_found" && (
        <div className={styles.summary}>
          <p className={styles.notFound} role="status">
            We couldn't find an order for that email and ZIP code.
          </p>
          <button type="button" className={styles.secondary} onClick={() => setPhase({ kind: "form" })}>
            Try again
          </button>
        </div>
      )}
    </section>
  );
}
